import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, concatMap } from 'rxjs/operators';

import { ApiService } from './api.service';
import { formatDate, getIfUseCentralizedStock } from './../helpers/util';
import {DispatchSaleNote, CartItem, ProductAttributes, ExchangeItem, ItemDetail} from '../interfaces';
import type {Date, Freight, EntityOrigins, StockInventory, Inventory, Item, CentralizedStockRequest, CentralizedStockResponse}
 from '../models';
import { Constants } from '../configs';
import moment from 'moment';
import { AuthService } from './auth.service';
import { SaleConfig } from '../configs/sale.config';
import { OECHSLE_CODE } from '../configs/company.config';

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  typeSend = Constants.SaleConfig;
  constructor(private apiService: ApiService, private authService: AuthService) { }
  /**
   * Servicio que obtiene la lista de entidades origen
   * @param destination código del destino
   * @param sku código del producto
   */
  getRoutesDispatch(destination: string, saleEntityCode: string, cartItem: CartItem, sku?: string, saleEntity?: any): Observable<EntityOrigins[]> {
    return this.apiService
      .get(`${this.apiService.stockIntegral}/${saleEntityCode}/${destination}/${sku}`)
      .pipe(map(response => {
        if (!saleEntity || Array.isArray(saleEntity)) {
          return response;
        } else {
          const originEntityAlreadyExist = response.find(i => i && i.entityCode === saleEntity.entityCode);
          const sameOriginAndDestination = saleEntity.entityCode === destination;
            if (saleEntity && !originEntityAlreadyExist) {
              if (cartItem.deliveryMode === SaleConfig.STORE_WITHDRAWAL) {
                const destinationDescription = (sameOriginAndDestination ? saleEntity.entityName : cartItem.destinationDescription);
                this.setOriginForSaleEntity(response, saleEntity, destinationDescription, destination, sameOriginAndDestination);
              } else {
                if (cartItem.deliveryMode === SaleConfig.HOME_DELIVERY && response && response.length > 0) {
                  const routeDescriptionDetail = response[0].route.split(Constants.routeConfig.characterBetweenRoutes);
                  const destinationDescription = routeDescriptionDetail[routeDescriptionDetail.length - 1];
                  this.setOriginForSaleEntity(response, saleEntity, destinationDescription, destination, sameOriginAndDestination);
                }
              }
            }
          return response;
        }

      }));
  }

  setOriginForSaleEntity(response: any, saleEntity: any, destinationDescription: string, destination: string, sameOriginAndDestination: boolean): any {
    response.push({
      entityCode: saleEntity.entityCode,
      entityName: saleEntity.entityName,
      entityType: saleEntity.entityType,
      route: saleEntity.entityName + Constants.routeConfig.characterBetweenRoutes + destinationDescription,
      routeDetail: sameOriginAndDestination ? [saleEntity.entityCode] : [saleEntity.entityCode, destination],
      routeId: '00000',
      shortName: saleEntity.entityName
    });
    return response;
  }

  /**
   * Retorna el stock del producto según la lista de entidades
   * @param product código del producto
   * @param destiny código del destino
   */

  getPriceService(skuCode, entity): Observable<any> {
    return this.apiService
      .get(`${this.apiService.stockDelivery}?skuCode=${skuCode}&entities=${entity}`)
      .pipe();
  }

  /**
   * Lista de stock disponible
   */
  stockProductsOrigins(request): Observable<StockInventory> {
    const endpointStock = `${this.apiService.stockDelivery}/products`;
    return this.apiService
      .post(endpointStock, request)
      .pipe();
  }

      /**
   * Lista de stock disponible api stock centralizado
   */
      centralizedStockProductsByOrigins(request: CentralizedStockRequest): Observable<StockInventory> {
        const endpointCentralizedStock = `${this.apiService.centralizedStock}/stock`;
        return this.apiService
          .post(endpointCentralizedStock, request)
          .pipe(map((res: CentralizedStockResponse)=>this.mapToApiStock(res)));
        }

    mapToApiStock(stockList: CentralizedStockResponse): StockInventory {
      const stockInventory: StockInventory  = {
        entityCode: null,
        skuCode: "",
        stock: stockList.stockDTOS.map(item => {
          return {
            cost: item.cost,
            entityCode: item.entityCode,
            line: item.productLine,
            price: item.price,
            quantity: item.quantityAvailable,
            quantityCurrent: item.quantityAvailable,
            skuCode: item.skuCode
          }
        })
      }
      return stockInventory;
      }

  /**
   * Obtener el stock y precio del producto sin consultar la ruta
   * @param skuCode Código de SKU
   * @param origins Lista de código de la entidad origen
   * @param saleEntity Entidad de venta
   */
  getStockPrices(skuCode: string, origins: string, saleEntity: string): Observable<Inventory[]> {
    // const endpointStock = `${this.apiService.stockDelivery}?skuCode=${skuCode}&saleEntity=${saleEntity}&entities=${origins}`;
    const list = origins.split(',').map(item => {
      return { entityCode: item, skuCode: skuCode };
    });
    const request: any = {};
    request.productOrigins = list;
    request.saleEntity = saleEntity;

    return this.stockProductsOrigins(request)
      .pipe(map(i => i.stock));
  }

  /**
   * Obtener la ruta y el stock de la entidad
   * @param cartItem
   * @param zoneId Id de la zona o código de la entidad destino
   * @param saleEntityCode Entidad de venta
   * @param saleEntities
   */
  getEntitiesStocks(cartItem: CartItem, zoneId: string, saleEntityCode: string, saleEntities: any): Observable<any> {
    return this.getRoutesDispatch(zoneId, saleEntityCode, cartItem, cartItem.skuCode,  saleEntities)
      .pipe(
        switchMap(response => {
          if (response.length) {
            const list = response.map(item => {
              return { entityCode: item.entityCode, skuCode: cartItem.skuCode };
            });

            const UseCentralizedStock = getIfUseCentralizedStock(saleEntityCode);
            let request: any = {};

            if (UseCentralizedStock) {
              request = {
                stocks: list,
                saleEntity: saleEntityCode
              }
            } else {
              request.productOrigins = list;
              request.saleEntity = saleEntityCode;
            }

            return (UseCentralizedStock
              ? this.centralizedStockProductsByOrigins(request)
              : this.stockProductsOrigins(request))
              .pipe(
                map((item: any) =>
                  response.map(element => {
                    if (element
                      && item && item.stock
                    ) {
                      const val = item.stock.find(
                        elem => elem.entityCode === element.entityCode,
                      );

                      if (val) {
                        return  { ...element, ...val };
                      } else {
                        return this.getStockFromSaleEntity(response, element, saleEntityCode, item, cartItem);
                      }
                    } else {
                      of([]);
                    }
                  }).filter(value => value)
                ),
              );
          } else {
            return of(response);
          }
        }),
      );
  }

  /**
   * Función que busca en el listado de stock si hay alguno que es del CD en caso no se haya informado
   * en el archivo de stock
   * @param listOfOrigins Listado de origenes
   * @param stockList Listado de stock obtenidos para los origenes existentes
   */
  searchStockCD(listOfOrigins, stockList) {
    let stockCD = null;
    const existListCd = listOfOrigins.filter(element => element.entityType === Constants.Entity.CD);
    if (existListCd && existListCd.length > 0) {
      for (const productCD of existListCd) {
          stockCD = stockList.stock
          .find(elem => elem.entityCode === productCD.entityCode);
            if (stockCD) {
              break;
            }
      }
    }
    return  stockCD;
  }


  /**
   * Función a la que se ingresa si no se ha informado el stock para la entidad de venta
   * @param listOfOrigins Listado de origenes
   * @param element entidad origen
   * @param saleEntityCode entidad de venta
   * @param stockList Listado de stock obtenidos para los origenes existentes
   */
  getStockFromSaleEntity(listOfOrigins, origin, saleEntityCode, stockList, cartItem: CartItem) {
    const existStock = stockList.length > 0;
    if (origin.entityCode === saleEntityCode) {
      const stockCD = this.searchStockCD(listOfOrigins, stockList);
      const stock = {
        cost: stockCD ? stockCD.cost : existStock ? (stockList.stock[0].cost) : cartItem.cost,
        entityCode: saleEntityCode,
        line: stockCD ? stockCD.line : existStock ? (stockList.stock[0].line) : cartItem.productLine,
        price: stockCD ? stockCD.price : existStock ? (stockList.stock[0].price) : cartItem.price,
        quantity: 0,
        skuCode: stockCD ? stockCD.skuCode : existStock ? (stockList.stock[0].skuCode) : cartItem.skuCode
      };
      return { ...origin, ...stock };
    } else {
      const stock = {
        cost: existStock ? (stockList.stock[0].cost) : cartItem.cost,
        entityCode: origin.entityCode,
        line: existStock ? (stockList.stock[0].line) : cartItem.productLine,
        price: existStock ? (stockList.stock[0].price) : cartItem.price,
        quantity: 0,
        skuCode: existStock ? (stockList.stock[0].skuCode) : cartItem.skuCode
      };
        return { ...origin, ...stock };
      }
  }



  /**
   * Función que agrupar las entidades destino con las entidades que tienes stocks
   * @param origin lista de orígenes
   * @param entitiesStock lista de orígenes con stock
   * @param entityCode entidad actual
   * @param state estado de la cotización
   * @param quantityUnits cantidad de productos del despacho
   */
  getStocksMasiva(origin: EntityOrigins[], entitiesStock: any[], entityCode: string, state: string, quantityUnits: number, data: any): any {
    data.stocks = origin.map(element => {
      if (element && entitiesStock.length) {
        const val = entitiesStock.find(
          elem => elem.entityCode === element.entityCode,
        );
        if (val) {
          if (val.entityCode === entityCode && state === 'PENDING_AUTHORIZATION' && val.quantity < quantityUnits) {
            data.stocksEdit = {
              entityCode: val.entityCode,
              quantity: quantityUnits,
              previous: val.quantity,
              destinyCode: ''
            };
            val.quantity = quantityUnits;
          }
          return { ...val, ...element };
        }
      } else {
        return;
      }
    }).filter(value => value);

    return data;
  }

  /**
   * Retorna un observable de fechas disponibles
   * @param data Valor tipo objeto
       (origin, skuCode, line, entityType, dispatchType, province, zone, quantity, weight, date, address)
   * @param day ? Valor tipo date
   */
  getAvailableDates(data: DispatchSaleNote, loadDates?: boolean, deliveryMode?: string, returnAttributes:boolean = false ): Observable<Date[]> {
    const propertyIsUndefined = Object.keys(data)
    .filter(i => !i.includes('date') && !i.includes('weight') && !i.includes('measurable'))
    .find(property => !data[property]);
    return propertyIsUndefined
        ? of([])
        : this.getAttributesCode(new Date(), [data], returnAttributes)
  }

  generateRangeDates(firstDate, validateDates:boolean): string[] {
    const countIterations = 45;
    const deliveryDate = [];
    for (let index = 1; index < countIterations; index++) {
      const date = new Date(firstDate);
      const newDate = date;
      newDate.setDate(date.getDate() + Number(index));
      const finalDate = moment(newDate).format('YYYY-MM-DD');
      if (validateDates) {
        if (finalDate != Constants.Order.SIX && finalDate != Constants.Order.SEVEN && finalDate != Constants.Order.EIGHT) {
          deliveryDate.push(finalDate);
        }
      } else {
        deliveryDate.push(finalDate);
      }
    }
    return deliveryDate;
  }

  getAvailableDatesMassive(data: DispatchSaleNote[], day = new Date()): Observable<Date[]> {
    // ===== return this.getDateForCapacities(day, data);
    return this.getAttributesCode(day, data);
  }

  /**
   * Retorna un observable de fechas disponibles
   * @param data Valor tipo objeto
   (origin, skuCode, line, entityType, dispatchType, province, zone, quantity, weight, date, address)
   * @param day ? Valor tipo date
   */
  getAvailableDatesFromDispatch(data: DispatchSaleNote): Observable<Date[]> {
    const valid = Object.keys(data).filter(i => !i.includes('date') && !i.includes('weight')).find(property => !data[property]);
    return !valid
      ? this.getAttributesCode(new Date(), [data],)
      : of([]);
  }

  getAttributesCode(day: any, request: DispatchSaleNote[], returnAttributes = false): Observable<any> {
    const body = request.map(product => product.skuCode);
    return this.apiService.post(this.apiService.searchProduct + '/attribute', body).pipe(
      concatMap((attribute: ProductAttributes[]) => this.getDateForCapacities(day, request, attribute, returnAttributes))
    );
  }

  addAttributeToRequest(request: DispatchSaleNote[], attributes: ProductAttributes[]): DispatchSaleNote[] {
     return request.map(product => {
        product.attributes = this.getAttributeCodeInAtrributeList(attributes);
        return product;
      });
  }

  getAttributeCodeInAtrributeList(attributes: ProductAttributes[]): any[] {
    return attributes.map(attributeList => {
      return { attributeCode: attributeList.attributeCode };
    });
  }

  /**
   * Retorna la primera fecha disponible según las capacidades
   * @param day Fecha actual
   * @param data Información de ruta, cantidad y producto
   */
   getDateForCapacities(day: any, request: DispatchSaleNote[], attributes: ProductAttributes[], returnAttributes): Observable<any> {

     const body = this.addAttributeToRequest(request, attributes);
    return attributes && attributes.length > 0 ? this.apiService
      .post(this.apiService.datesDelivery, { purchaseDate: formatDate(day), items: body })
      .pipe(
        map(resp => {
          const userCompany = this.authService.getCompanyCode();
          if (resp && resp[0] && userCompany == OECHSLE_CODE && request[0].zone.includes('ZN')) {
            resp[0].deliveryDates = resp[0].deliveryDates.filter(date => this.findDate(date));
          }
        return returnAttributes ? {date: resp , attributes : body[0].attributes} : resp;
        })
      ) : of([]);
   }

  findDate(dateCapacity: string): boolean {
    const listDates = ['2023-12-24', '2023-12-25','2023-12-26','2023-12-31','2024-01-01','2024-01-02']
    const dateFound = listDates.find(date => date == dateCapacity);
    return dateFound? false: true;
  }

  /**
   * Retorna la lista de fechas disponibles por 45 diás aprox
   * @param request
   */
  /*getListDateAvailable(request: DispatchSaleNote, firstDate: string, showDates?:boolean): Observable<string[]> {
    const body = {
      origin: request.origin,
      dispatchType: request.dispatchType,
      zone: request.zone,
      deliveryDate: firstDate,
      attributes: request.attributes
    }
    return this.apiService
    .post(this.apiService.loadDatesDelivery, body)
    .pipe(map(response => {
      return response.calendarDates;
    }));
  }*/

  /**
   * Retorna un observable de entidades destino a partir de una entidad o dirección origen
   * lista de rutas
   * @param origin Código de la entidad
   */
  getEntitiesDestiny(origin): Observable<any[]> {
    return this.apiService
      .get(`${this.apiService.entityRoutes}/?origin=${origin}`)
      .pipe();
  }

  /**
   * Retorna un observable de flete
   * @param data Valor tipo objeto
     (origin, skuCode, line, entityType, dispatchType, province, zone, quantity, weight, date, address)
   */
  getShipping(data): Observable<Freight | null> {
    const valid = Object.keys(data).filter(i => !i.includes('weight')).find(property => !data[property]);
    return !valid
      ? this.apiService
        .post(this.apiService.freightDelivery, data)
        .pipe()
      : of(null);
  }

  getStore(origin): Observable<any[]> {
    return this.apiService
      .get(`${this.apiService.entityRoutesConfigure}/?entity_origin=${origin}`)
      .pipe();
  }
}
